import Phaser from 'phaser';
import { objectArt, mouseArt } from '../art/worldArt';
import { WORLD } from '../config/constants';
import { app } from '../app';
import type { RoomId } from '../core/GameState';
import { staffOccupies, staffObject } from '../core/StaffNavigation';
import { ROOMS, type RoomDef, type RoomObject } from './rooms';
import { facingForStep, tileCenter, toScreen, type Facing, type Tile } from './Grid';
import { tilePoint, pointTile, project, unproject, route, distance, nearestFree, recoverPosition, segmentClear, slide, withActors, PLAYER_SPEED, type Point } from './Movement';
import { drawRoomScenery } from './RoomScenery';

interface ObjectView {
  def: RoomObject;
  art: Phaser.GameObjects.Image;
  gfx: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
}

interface StaffView {
  id: string;
  body: Phaser.GameObjects.Container;
  art: Phaser.GameObjects.Image;
  gfx: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
}

export class WorldScene extends Phaser.Scene {
  private room!: RoomDef;
  private interactionPrompt!: HTMLDivElement;
  /** Objects present given the skills owned */
  private objects: RoomObject[] = [];
  private originX = 0;
  private originY = 0;
  private floor: Phaser.GameObjects.Container | null = null;
  private roomTitle: Phaser.GameObjects.Text | null = null;
  private views: ObjectView[] = [];
  private player!: Phaser.GameObjects.Container;
  private playerArt!: Phaser.GameObjects.Image;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private playerGfx!: Phaser.GameObjects.Graphics;
  private tile: Tile = { x: 0, y: 0 };
  private facing: Facing = 'se';
  private moving = false;
  private pending: RoomObject | null = null;
  private hover: RoomObject | null = null;
  private transitioning = false;
  private position: Point = tilePoint({x:4,y:4});
  private pixelPath: Point[] = [];
  private staffViews: StaffView[] = [];
  private blockedIndicator: Phaser.GameObjects.Text | null = null;

  constructor() {
    super('world');
  }

  preload(): void {
    for (const room of Object.values(ROOMS)) for (const obj of room.objects) {
      if (obj.kind !== 'door' && !this.textures.exists('art-' + obj.id)) this.load.svg('art-' + obj.id, 'data:image/svg+xml;base64,' + btoa(objectArt(obj.id)));
    }
    for (const role of ['player', 'barn', 'vat', 'cellar', 'idle']) for (const face of ['s', 'se', 'e', 'ne', 'n']) {
      this.load.svg('mouse-' + role + '-' + face, 'data:image/svg+xml;base64,' + btoa(mouseArt(role, face)));
    }
  }

  create(): void {
    app.scene = this;
    this.interactionPrompt=document.createElement('div');
    this.interactionPrompt.className='interaction-prompt';
    this.interactionPrompt.setAttribute('role','status');
    this.interactionPrompt.hidden=true;
    document.body.appendChild(this.interactionPrompt);
    this.events.once('shutdown',()=>this.interactionPrompt.remove());
    this.player = this.add.container(0, 0);
    this.playerGfx = this.add.graphics();
    this.playerArt = this.add.image(0, 0, 'mouse-player-se').setOrigin(0.5, 0.92).setDisplaySize(83, 80);
    this.player.add([this.playerGfx, this.playerArt]);
    this.buildRoom(app.game.state.room, app.game.state.playerTile);
    this.scale.on('resize', () => this.frameRoom());

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.onPointerDown(p));
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => this.onPointerMove(p));
    app.game.on('change', () => this.refreshObjects());

    app.game.on('room', (room) => {
      if (!this.transitioning) this.buildRoom(room, app.game.state.playerTile);
    });
  }

  update(_time: number, delta: number): void {
    this.advanceMovement(Math.min(.05, delta / 1000));
    app.game.tick(Math.min(0.5, delta / 1000));
    this.refreshStaff();
    this.refreshObjects();
    this.updateInteractionPrompt();
    this.playerArt.y = this.moving && !app.ui.playerInputBlocked && !this.reducedMotion ? Math.sin(this.time.now / 60) * 2 : 0;
  }

  // ------------------------------------------------------------ building

  buildRoom(id: RoomId, tile: Tile): void {
    this.room = ROOMS[id];
    this.objects = this.room.objects.filter((o) => !o.requiresSkill || app.game.hasSkill(o.requiresSkill));
    const { cols, rows } = this.room;
    this.originX = WORLD.WIDTH / 2 - ((cols - rows + 1) * WORLD.TILE_W) / 4;
    this.originY = (WORLD.HEIGHT - ((cols + rows) * WORLD.TILE_H) / 2) / 2 + 30;
    this.cameras.main.setBackgroundColor(this.room.background);

    this.floor?.destroy();
    this.roomTitle?.destroy();
    this.blockedIndicator?.destroy();
    for (const v of this.views) {
      v.gfx.destroy();
      v.art.destroy();
      v.label.destroy();
    }
    this.views = [];
    for (const v of this.staffViews) v.body.destroy();
    this.staffViews = [];
    this.pixelPath = [];
    this.pending = null;
    this.hover = null;
    this.moving = false;
    this.tweens.killTweensOf(this.player);
    app.game.state.playerMovingTo = null;

    this.drawFloor();
    for (const def of this.objects) this.views.push(this.createObjectView(def));

    this.roomTitle = this.add
      .text(WORLD.WIDTH / 2, this.originY - 40, this.room.name, {
        fontFamily: 'system-ui, Segoe UI, sans-serif',
        fontSize: '22px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(1000)
      .setAlpha(0.85);

    const saved=app.game.state.playerPosition;
    this.position=recoverPosition(this.room,app.game.state.skills, saved && pointTile(saved).x === tile.x && pointTile(saved).y === tile.y ? saved : tilePoint(tile));
    const start = pointTile(this.position);
    this.tile = { ...start };
    app.game.state.playerPosition={...this.position};
    app.game.setPlayerTile(this.tile);
    const projected = project(this.position);
    const c = {sx:projected.x+this.originX,sy:projected.y+this.originY};
    this.player.setPosition(c.sx, c.sy);
    this.player.setDepth(this.tile.x + this.tile.y + 0.5);
    this.drawPlayer();
    this.blockedIndicator = this.add.text(WORLD.WIDTH / 2, 90, '', {
      fontFamily: 'system-ui, Segoe UI, sans-serif', fontSize: '17px', color: '#ffe2a3',
      backgroundColor: '#4a261d', padding: { x: 14, y: 8 }, stroke: '#221510', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(1001).setVisible(false);
    this.refreshStaff();
    this.frameRoom();
  }

  /** Keep logical tiles fixed while fitting all actionable art to the live viewport. */
  private frameRoom(): void {
    if (!this.room || !this.floor) return;
    const width = this.scale.width, height = this.scale.height;
    const hudHeight = document.getElementById('hud')?.getBoundingClientRect().height ?? 58;
    const bottomHeight = document.getElementById('bottombar')?.getBoundingClientRect().height ?? 80;
    document.documentElement.style.setProperty('--hud-height',hudHeight+'px');
    const top = hudHeight + 12;
    const usableHeight = Math.max(80, height - top - bottomHeight - 12);
    const corners = [toScreen(0,0,this.originX,this.originY), toScreen(this.room.cols,0,this.originX,this.originY),
      toScreen(0,this.room.rows,this.originX,this.originY), toScreen(this.room.cols,this.room.rows,this.originX,this.originY)];
    let left = Math.min(...corners.map(p => p.sx)) - 60, right = Math.max(...corners.map(p => p.sx)) + 60;
    let upper = Math.min(...corners.map(p => p.sy)) - 100, lower = Math.max(...corners.map(p => p.sy)) + 60;
    for (const v of this.views) for (const obj of [v.art,v.label]) {
      const r = obj.getBounds(); left=Math.min(left,r.left);right=Math.max(right,r.right);upper=Math.min(upper,r.top);lower=Math.max(lower,r.bottom);
    }
    if (this.roomTitle) { const r=this.roomTitle.getBounds();upper=Math.min(upper,r.top); }
    const zoom = Math.min(Math.max(50,width-48)/(right-left), Math.max(50,usableHeight-48)/(lower-upper));
    this.cameras.main.setViewport(0,top,width,usableHeight).setZoom(zoom).centerOn((left+right)/2,(upper+lower)/2);
    document.getElementById('game')?.style.setProperty('--room-bg', this.room.background);
    this.blockedIndicator?.setPosition((left+right)/2,upper+40);
  }

  private refreshStaff(): void {
    const employees = app.game.state.staff.employees.filter((e) => e.presentToday && e.location?.room === this.room.id);
    for (const v of this.staffViews) {
      if (!employees.some((e) => e.id === v.id)) v.body.destroy();
    }
    this.staffViews = this.staffViews.filter((v) => employees.some((e) => e.id === v.id));
    for (const e of employees) {
      let view = this.staffViews.find((v) => v.id === e.id);
      if (!view) {
        const body = this.add.container(0, 0);
        const gfx = this.add.graphics();
        const label = this.add.text(0, -78, '', { fontFamily: 'system-ui, Segoe UI, sans-serif', fontSize: '11px', color: '#ffffff', stroke: '#000000', strokeThickness: 3 }).setOrigin(0.5);
        const art = this.add.image(0, 0, 'mouse-idle-se').setOrigin(0.5, 0.92).setDisplaySize(78, 75);
        body.add([art, gfx, label]);
        view = { id: e.id, body, art, gfx, label };
        this.staffViews.push(view);
      }
      const p = { x: e.location!.x, y: e.location!.y };
      const moving = e.jobStatus === 'walking' || e.jobStatus === 'carrying';
      if (moving && e.navStep) {
        p.x += (e.navStep.to.x - e.navStep.from.x) * e.navStep.progress;
        p.y += (e.navStep.to.y - e.navStep.from.y) * e.navStep.progress;
      }
      const projected=e.position?project(e.position):null;
      const c=projected?{sx:projected.x+this.originX,sy:projected.y+this.originY}:tileCenter(p.x,p.y,this.originX,this.originY);
      view.body.setPosition(c.sx,c.sy).setDepth(e.position?(e.position.x+e.position.y)/64-.5:p.x+p.y+1);
      view.gfx.clear();
      const destination=e.continuousPath?.[0];
      const face=destination&&e.position?facingForStep(Math.sign(destination.x-e.position.x),Math.sign(destination.y-e.position.y)):'se';
      const mirrored = ['w', 'sw', 'nw'].includes(face);
      const sourceFace = face === 'w' ? 'e' : face === 'sw' ? 'se' : face === 'nw' ? 'ne' : face;
      view.art.setTexture('mouse-' + (e.station ?? 'idle') + '-' + sourceFace).setFlipX(mirrored);
      view.art.y = !this.reducedMotion && (moving || e.jobStatus === 'working') ? Math.sin(this.time.now / (moving ? 70 : 180)) * 2 : 0;
      if (e.jobStatus === 'working') {
        view.gfx.fillStyle(0x1b1b1b, 0.8);
        view.gfx.fillRect(-30, -68, 60, 6);
        view.gfx.fillStyle(0x2ecc71, 1);
        view.gfx.fillRect(-29, -67, 58 * Math.max(0, Math.min(1, e.jobProgress ?? 0)), 4);
      }
      const carrying = !!e.job && (e.job.phase === 'deliver' || e.jobStatus === 'carrying') && (!!e.job.batch || e.job.products.length > 0);
      if (carrying) {
        const milk = e.job?.products.some((item) => item.kind === 'milk');
        view.gfx.fillStyle(milk ? 0xe4edf2 : 0xe8bf55, 1);
        view.gfx.fillRoundedRect(5, -26, 20, 17, 4);
        view.gfx.lineStyle(2, milk ? 0xa8c1d1 : 0xac7628, 1);
        view.gfx.strokeRoundedRect(5, -26, 20, 17, 4);
      }
      view.label.setText(`${e.jobStatus === 'waiting' ? '! ' : ''}${e.name} · ${e.jobLabel || 'Idle'}`);
      view.label.setColor(e.jobStatus === 'waiting' ? '#ffdc8e' : '#ffffff');
    }
    const blocked = employees.find((e) => e.jobStatus === 'waiting' && (e.blockedReason || e.navigationBlocked));
    if (blocked && this.blockedIndicator) {
      const station = blocked.navTarget ? staffObject(app.game.state, blocked.navTarget) : undefined;
      this.blockedIndicator.setText(`! ${station?.label ?? blocked.name}: ${blocked.blockedReason || blocked.navigationBlocked}`)
        .setAlpha(1).setVisible(true);
    } else this.blockedIndicator?.setVisible(false);
  }

  private drawFloor(): void { this.floor=drawRoomScenery(this,this.room,this.originX,this.originY,app.game.state.skills); }

  private diamond(x: number, y: number, lift: number): Phaser.Types.Math.Vector2Like[] {
    const a = toScreen(x, y, this.originX, this.originY);
    const b = toScreen(x + 1, y, this.originX, this.originY);
    const c = toScreen(x + 1, y + 1, this.originX, this.originY);
    const d = toScreen(x, y + 1, this.originX, this.originY);
    return [
      { x: a.sx, y: a.sy - lift },
      { x: b.sx, y: b.sy - lift },
      { x: c.sx, y: c.sy - lift },
      { x: d.sx, y: d.sy - lift },
    ];
  }

  private footprint(def: RoomObject, lift: number): Phaser.Types.Math.Vector2Like[] {
    const a = toScreen(def.x, def.y, this.originX, this.originY);
    const b = toScreen(def.x + def.w, def.y, this.originX, this.originY);
    const c = toScreen(def.x + def.w, def.y + def.h, this.originX, this.originY);
    const d = toScreen(def.x, def.y + def.h, this.originX, this.originY);
    return [
      { x: a.sx, y: a.sy - lift },
      { x: b.sx, y: b.sy - lift },
      { x: c.sx, y: c.sy - lift },
      { x: d.sx, y: d.sy - lift },
    ];
  }

  private createObjectView(def: RoomObject): ObjectView {
    const gfx = this.add.graphics();
    this.drawObject(gfx, def, false);
    const base = this.footprint(def, 0);
    const cxBase = (base[0].x + base[2].x) / 2;
    const cyBase = (base[0].y + base[2].y) / 2;
    const size = def.kind === 'rack' ? 150 : def.w === 1 && def.h === 1 ? 112 : def.id === 'pasture' ? 250 : 220;
    const art = this.add.image(cxBase, cyBase + 18, def.kind === 'door' ? '__WHITE' : 'art-' + def.id)
      .setOrigin(0.5, 0.88).setDisplaySize(size, size * 200 / 260).setVisible(def.kind !== 'door');
    const top = this.footprint(def, def.z);
    const cx = (top[0].x + top[2].x) / 2;
    const cy = (top[0].y + top[2].y) / 2;
    const label = this.add
      .text(cx, def.kind === 'door' ? cy + 4 : cyBase + 33, def.label, {
        fontFamily: 'system-ui, Segoe UI, sans-serif',
        fontSize: '13px',
        color: '#fff4d8',
        stroke: '#352b32',
        strokeThickness: 3,
      })
      .setOrigin(0.5);
    const key = def.kind === 'door' ? -5 : def.x + def.y + def.w + def.h - 2 + 0.4;
    art.setDepth(key);
    gfx.setDepth(key + 0.01);
    label.setDepth(key + 0.02);
    return { def, gfx, art, label };
  }

  private drawObject(gfx: Phaser.GameObjects.Graphics, def: RoomObject, hovered: boolean): void {
    gfx.clear();
    if (def.kind === 'door') {
      if (hovered) {
        gfx.lineStyle(3, 0xffffff, 1);
        gfx.strokePoints(this.diamond(def.x, def.y, 0), true);
      }
      return;
    }
    const base = this.footprint(def, 0);
    const cx = (base[0].x + base[2].x) / 2, cy = (base[0].y + base[2].y) / 2;
    if (hovered) { gfx.lineStyle(3, 0xf3c655, 1); gfx.strokePoints(base, true); }
    const state = app.game.state;
    if (def.kind === 'rack') {
      const total = state.cellar.racks.reduce((n, r) => n + r.qty, 0);
      const count = Math.min(6, Math.max(0, def.id === 'rack_west' ? Math.ceil(total / 2) : Math.floor(total / 2)));
      gfx.fillStyle(0xf3c655); gfx.lineStyle(2,0x9d6947);
      for (let i = 0; i < count; i++) { const x = cx - 30 + (i % 3) * 22, y = cy - 59 + Math.floor(i / 3) * 25; gfx.fillRoundedRect(x,y,17,10,3); gfx.strokeRoundedRect(x,y,17,10,3); }
    }
    if (def.id === 'vat' || def.id === 'table' || def.id === 'tub') {
      const batch = state[def.id].batch;
      if (batch) {
        gfx.fillStyle(0x356449); gfx.fillCircle(cx + 42, cy - 37, 6);
        if (def.id === 'table') { gfx.fillStyle(0xf3c655); gfx.lineStyle(2,0xc97943); gfx.fillRoundedRect(cx-20,cy-55,38,17,5); gfx.strokeRoundedRect(cx-20,cy-55,38,17,5); }
      }
      if (def.id === 'vat' && state.vat.durability <= 0) { gfx.fillStyle(0xa43f40); gfx.fillTriangle(cx+34,cy-28,cx+50,cy-28,cx+42,cy-44); }
    }
  }

  refreshObjects(): void {
    for (const v of this.views) {
      this.drawObject(v.gfx, v.def, v.def === this.hover);
      const b=v.art.getBounds();v.art.setAlpha(v.art.depth>this.player.depth&&b.contains(this.player.x,this.player.y-25)?.55:1);
      if (v.def.id === 'bessie' || v.def.id === 'pasture') {
        const grazing = app.game.state.bessie.location === 'pasture';
        v.art.setTexture('art-' + (v.def.id === 'pasture' && grazing ? 'bessie' : v.def.id));
        v.art.setVisible(v.def.id !== 'bessie' || !grazing);
        v.label.setText(v.def.id === 'pasture' ? (grazing ? 'Bessie · grazing (+15% milk)' : 'Sunny Pasture · move Bessie here') : (grazing ? 'Bessie is in Sunny Pasture' : 'Bessie'));
      }
    }
  }

  private drawPlayer(): void {
    const g = this.playerGfx;
    g.clear(); g.fillStyle(0x352b32,0.18); g.fillEllipse(0,0,37,14);
    const f = this.facing;
    const mirrored = ['w','sw','nw'].includes(f);
    const sourceFace = f === 'w' ? 'e' : f === 'sw' ? 'se' : f === 'nw' ? 'ne' : f;
    this.playerArt.setTexture('mouse-player-' + sourceFace).setFlipX(mirrored);
  }

  // ------------------------------------------------------------ queries

  walkable(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.room.cols || y >= this.room.rows) return false;
    const obj = this.objectAt(x, y);
    return (!obj || obj.kind === 'door') && !staffOccupies(app.game.state, this.room.id, x, y);
  }

  objectAt(x:number,y:number):RoomObject|null {return this.objects.find(o=>x>=o.x&&x<o.x+o.w&&y>=o.y&&y<o.y+o.h)??null;}

  private adjacentGoals(def: RoomObject): Set<string> {
    const goals = new Set<string>();
    for (let y = def.y - 1; y <= def.y + def.h; y++) {
      for (let x = def.x - 1; x <= def.x + def.w; x++) {
        const inside = x >= def.x && x < def.x + def.w && y >= def.y && y < def.y + def.h;
        const corner = (x === def.x - 1 || x === def.x + def.w) && (y === def.y - 1 || y === def.y + def.h);
        if (inside || corner) continue;
        if (this.walkable(x, y)) goals.add(`${x},${y}`);
      }
    }
    return goals;
  }

  private isAdjacent(def: RoomObject): boolean {
    const nearest={x:Math.max(def.x*64,Math.min((def.x+def.w)*64,this.position.x)),y:Math.max(def.y*64,Math.min((def.y+def.h)*64,this.position.y))};
    return Math.hypot(nearest.x-this.position.x,nearest.y-this.position.y)<=48;
  }

  nearbyObject(): RoomObject | null {
    return this.objects.filter(o=>o.kind!=='door'&&this.isAdjacent(o)&&!(o.id==='bessie'&&app.game.state.bessie.location==='pasture')).sort((a,b)=>this.objectDistance(a)-this.objectDistance(b))[0]??null;
  }

  private objectDistance(o:RoomObject):number {
    const x=Math.max(o.x*64,Math.min((o.x+o.w)*64,this.position.x));
    const y=Math.max(o.y*64,Math.min((o.y+o.h)*64,this.position.y));
    return Math.hypot(x-this.position.x,y-this.position.y);
  }

  private updateInteractionPrompt():void {
    const obj=!this.transitioning&&!app.ui.panels.openId&&!app.ui.skillTree.isOpen?this.nearbyObject():null;
    this.interactionPrompt.hidden=!obj;
    if(!obj)return;
    const text='E to interact · '+obj.label;
    if(this.interactionPrompt.textContent!==text)this.interactionPrompt.textContent=text;
    const camera=this.cameras.main, zero=camera.getWorldPoint(0,0);
    const half=this.interactionPrompt.offsetWidth/2+8;
    const x=Math.max(half,Math.min(this.scale.width-half,(this.player.x-zero.x)*camera.zoomX));
    const hud=document.getElementById('hud')?.getBoundingClientRect().bottom??58;
    const y=Math.max(hud+this.interactionPrompt.offsetHeight+8,(this.player.y-zero.y)*camera.zoomY-85*camera.zoomY-12);
    this.interactionPrompt.style.left=x+'px';this.interactionPrompt.style.top=y+'px';
  }

  playerScreenX(): number {
    const camera = this.cameras.main;
    return (this.player.x-camera.getWorldPoint(0,0).x)*camera.zoomX;
  }

  // ------------------------------------------------------------- input

  /** Hit-test the drawn boxes (top face and both side faces), front-most first. */
  private objectUnderPointer(sx: number, sy: number): RoomObject | null {
    // The visible exit caption is an intentional target, including its edges.
    for(const v of this.views) if(v.def.kind==='door'){
      const b=v.label.getBounds();if(sx>=b.left-8&&sx<=b.right+8&&sy>=b.top-8&&sy<=b.bottom+8)return v.def;
    }
    const sorted = [...this.views].sort((a, b) => b.gfx.depth - a.gfx.depth);
    for (const v of sorted) {
      const def = v.def;
      if (def.kind === 'door') {
        if (Phaser.Geom.Polygon.Contains(new Phaser.Geom.Polygon(this.diamond(def.x, def.y, 0) as Phaser.Geom.Point[]), sx, sy)) return def;
        continue;
      }
      const base = this.footprint(def, 0);
      const top = this.footprint(def, def.z);
      if (v.art.visible && v.art.getBounds().contains(sx, sy)) return def;
      const polys = [top, [base[1], base[2], top[2], top[1]], [base[3], base[2], top[2], top[3]]];
      for (const pts of polys) {
        if (Phaser.Geom.Polygon.Contains(new Phaser.Geom.Polygon(pts as Phaser.Geom.Point[]), sx, sy)) return def;
      }
    }
    return null;
  }

  private onPointerDown(p: Phaser.Input.Pointer): void {
    if (this.transitioning || app.ui.playerInputBlocked) return;
    const point = this.cameras.main.getWorldPoint(p.x,p.y);
    const obj = this.objectUnderPointer(point.x, point.y);
    if (obj) {
      this.interact(obj);
      return;
    }
    this.goToPixel(unproject({x:point.x-this.originX,y:point.y-this.originY}));
  }

  private onPointerMove(p: Phaser.Input.Pointer): void {
    if (app.ui.playerInputBlocked) return;
    const point = this.cameras.main.getWorldPoint(p.x,p.y);
    const obj = this.objectUnderPointer(point.x, point.y);
    if (obj === this.hover) return;
    const prev = this.hover;
    this.hover = obj;
    for (const v of this.views) {
      if (v.def === prev || v.def === obj) this.drawObject(v.gfx, v.def, v.def === obj);
    }
    this.input.setDefaultCursor(obj ? 'pointer' : 'default');
  }

  private collisionRoom():RoomDef {
    return withActors(this.room,app.game.state.staff.employees.filter(e=>e.presentToday&&(e.position?.room??e.location?.room)===this.room.id).map(e=>e.position??tilePoint(e.location!)));
  }

  cancelRoute(): void { this.pixelPath=[];this.pending=null;this.moving=false;app.game.state.playerMovingTo=null; }

  interact(obj: RoomObject): void {
    if(app.ui.playerInputBlocked)return;
    this.cancelRoute();
    if(obj.kind==='door'){this.goTo({x:obj.x,y:obj.y});return;}
    if(this.isAdjacent(obj)){app.ui.openObject(obj.id==='spaceship'?'terminal':obj.kind);return;}
    let best:Point[]|null=null,bestCost=Infinity;
    for(const key of this.adjacentGoals(obj)){
      const [x,y]=key.split(',').map(Number),target=tilePoint({x,y}),r=route(this.collisionRoom(),app.game.state.skills,this.position,target);
      if(!r)continue;let cost=0,p=this.position;for(const q of r){cost+=distance(p,q);p=q;}if(cost<bestCost){bestCost=cost;best=r;}
    }
    if(!best){app.game.toast('Cannot reach '+obj.label+'.','info');return;}
    app.ui.closePanel();this.pixelPath=best;this.pending=obj;
  }

  goTo(target:Tile):void { this.goToPixel(tilePoint(target)); }
  goToPixel(target:Point):void {
    if(this.transitioning||app.ui.playerInputBlocked)return;
    this.cancelRoute();
    if(target.x<0||target.y<0||target.x>this.room.cols*64||target.y>this.room.rows*64){app.game.toast('Stay inside the room boundary.','info');return;}
    const end=nearestFree(this.collisionRoom(),app.game.state.skills,target);
    const path=end&&route(this.collisionRoom(),app.game.state.skills,this.position,end);
    if(!path){app.game.toast('That spot is outside the walking area or blocked.','info');return;}
    app.ui.closePanel();this.pixelPath=path;
    app.game.state.playerMovingTo=pointTile(end!);
  }

  private advanceMovement(dt:number):void {
    if(this.transitioning||app.ui.playerInputBlocked){this.moving=false;return;}
    const before={...this.position},held=app.ui.heldVector();
    if(held){this.cancelRoute();const d=unproject({x:held.x*PLAYER_SPEED*dt,y:held.y*PLAYER_SPEED*dt});this.position=slide(this.collisionRoom(),app.game.state.skills,this.position,d);}
    else {
      let budget=PLAYER_SPEED*dt;
      while(this.pixelPath.length&&budget>0){const target=this.pixelPath[0],d=distance(this.position,target),t=d?Math.min(1,budget/d):1;
        const next={x:this.position.x+(target.x-this.position.x)*t,y:this.position.y+(target.y-this.position.y)*t};
        if(!segmentClear(this.collisionRoom(),app.game.state.skills,this.position,next)){
          const destination=this.pixelPath[this.pixelPath.length-1];
          const reroute=route(this.collisionRoom(),app.game.state.skills,this.position,destination);
          if(reroute)this.pixelPath=reroute;this.moving=false;break;
        }
        this.position=next;budget-=d*t;if(t===1)this.pixelPath.shift();else break;
      }
    }
    this.moving=distance(before,this.position)>.001;
    if(this.moving){const v={x:this.position.x-before.x,y:this.position.y-before.y};this.facing=facingForStep(Math.sign(v.x),Math.sign(v.y));this.drawPlayer();}
    this.tile=pointTile(this.position);app.game.state.playerPosition={...this.position};app.game.state.playerTile={...this.tile};
    const c=project(this.position);this.player.setPosition(c.x+this.originX,c.y+this.originY).setDepth((this.position.x+this.position.y)/64-.5);
    const door=this.objectAt(this.tile.x,this.tile.y);
    if(this.moving&&door?.kind==='door'&&door.target){this.transition(door.target.room,door.target.tile);return;}
    if(!this.pixelPath.length){app.game.state.playerMovingTo=null;if(this.pending){const obj=this.pending;this.pending=null;if(this.isAdjacent(obj))app.ui.openObject(obj.id==='spaceship'?'terminal':obj.kind);}}
  }

  private transition(room: RoomId, tile: Tile): void {
    this.transitioning = true;
    this.pixelPath = [];
    this.pending = null;
    this.pixelPath = [];
    app.ui.closePanel();
    app.ui.fade(
      () => {
        app.game.setRoom(room, tile);
        this.buildRoom(room, tile);
      },
      () => {
        this.transitioning = false;
      },
    );
  }
}
